import moment from 'moment';
import IndexReferenceType from '../IndexReferenceType';
import BrineLease from '../contract_types/BrineLease';
import Acknowledgement from '../leasing/Acknowledgement';
import LeaseClauseType from '../LeaseClauseType';
import ContractAddendumClause from 'src/components/project/sections/LeasingComponents/redesign/ContractGenerationModels/ContractAddendumClause';
import LeaseClause from '../LeaseClause';
import LocationModel from '../leasing/LocationModel';
import MathUtil from 'src/util/MathUtil';
import ContractGenerationTractDetail from 'src/components/project/sections/LeasingComponents/redesign/ContractGenerationModels/ContractGenerationTractDetail';

export default class SaltwerxBrineLeaseProps {
    leaseNumber: string;
    leasedBy: string;
    primaryRentalTerm: string;
    primaryRentalUnit: string;
    primaryRentalTotal: string;
    primaryRentalTotalWords: string;
    primaryRentalTotalCurrency: string;
    primaryRentalBonusUnits: string;
    leaseDateDo: string;
    leaseDateMonthName: string;
    leaseDateYear: string;
    leaseDateFull: string;
    longLeaseDate: string;
    effectiveDateDo: string;
    effectiveDateMonthName: string;
    effectiveDateYear: string;
    effectiveDateFull: string;
    longEffectiveDate: string;
    ownersOnDocument: string;
    ownersAddressOnDocument: string;
    lessorAka: string;
    lesseeName: string;
    lesseeAddress: string;
    legalDescription: string;
    originalGrossAcres: string;
    locationCounties: string;
    locationState: string;
    locationCountiesUpper: string;
    netAcres: string;
    grossAcres: string;
    currentDate: string;
    
    includedTract?: ContractGenerationTractDetail;
    acknowledgements?: Acknowledgement[];
    tracts?: ContractGenerationTractDetail[];
    additionalProvisions?: string[];
    adHocClauses?: string[];

    static from = (
        lease: BrineLease, 
        measurementTypes: IndexReferenceType[], 
        leaseClauseTypes?: LeaseClauseType[], 
        contractClauses?: ContractAddendumClause[],
        location?: LocationModel,
        netAcres?: number
    ): SaltwerxBrineLeaseProps => {
        const leaseProps = new SaltwerxBrineLeaseProps();
        const leasehold = lease.brineLeaseHolds[0];
        const primaryUnitMeasureName = measurementTypes.find(mt => mt.id === leasehold.primaryUnitTypeId)?.name.toLowerCase();
        const primaryRentalMeasureName = measurementTypes.find((mt) => mt.id === leasehold.primaryRentalFeeTypeId)?.name;

        leaseProps.leaseNumber = lease.leaseNumber.toString();
        leaseProps.leasedBy = lease.leasedBy;
        leaseProps.primaryRentalTerm = leasehold.primaryRentalTerm.toString();
        leaseProps.primaryRentalUnit = primaryUnitMeasureName.substring(0, primaryUnitMeasureName.length - 1);
        leaseProps.primaryRentalBonusUnits = primaryRentalMeasureName === 'perAcre' ? 'per net mineral acre' : '';
        leaseProps.primaryRentalTotal = (Math.round(leasehold.primaryRentalTotal * 100) / 100).toFixed(2);
        leaseProps.primaryRentalTotalCurrency = MathUtil.numberToCurrency(leasehold.primaryRentalTotal);
        leaseProps.leaseDateDo = moment(lease.leaseDate).format('Do');
        leaseProps.leaseDateMonthName = moment(lease.leaseDate).format('MMMM');
        leaseProps.leaseDateYear = moment(lease.leaseDate).format('YYYY');
        leaseProps.leaseDateFull = moment(lease.leaseDate).format('DD/MM/YYYY');
        leaseProps.longLeaseDate = `${moment(lease.leaseDate).format('Do')} day of ${moment(lease.leaseDate).format('MMMM')}, ${moment(lease.leaseDate).format('YYYY')}`;
        leaseProps.effectiveDateDo = moment(lease.effectiveDate).format('Do');
        leaseProps.effectiveDateMonthName = moment(lease.effectiveDate).format('MMMM');
        leaseProps.effectiveDateYear = moment(lease.effectiveDate).format('YYYY');
        leaseProps.effectiveDateFull = moment(lease.effectiveDate).format('DD/MM/YYYY');
        leaseProps.currentDate = new Date().getFullYear().toString();
        leaseProps.longEffectiveDate = `${moment(lease.effectiveDate).format('Do')} day of ${moment(lease.effectiveDate).format('MMMM')}, ${moment(lease.effectiveDate).format('YYYY')}`;
        leaseProps.ownersOnDocument = lease.ownersOnDocument;
        leaseProps.ownersAddressOnDocument = lease.ownersAddressOnDocument;
        leaseProps.lessorAka = lease.lessorAka;
        leaseProps.lesseeName = lease.lesseeName;
        leaseProps.lesseeAddress = `${lease.lesseeAddressLine1}, ${lease.lesseeCity}, ${lease.lesseeState} ${lease.lesseeZipCode}`;
        leaseProps.legalDescription = lease.legalDescription;
        leaseProps.originalGrossAcres = lease.originalGrossAcres.toString();
        leaseProps.acknowledgements = lease.acknowledgements;
        leaseProps.netAcres = netAcres ? MathUtil.cleanNumber(netAcres || 0, 7) : '0';

        leaseProps.grossAcres = lease.tracts?.reduce((acc: number, tract: ContractGenerationTractDetail) => {
            let acreage = acc;
            if (tract.includeTract === true) {
                let tempGrossAcres = parseFloat(tract.grossAcres + '') || 0;
                acreage += tempGrossAcres;
            }
    
            return acreage;
        }, 0).toString();
        leaseProps.includedTract = lease.tracts?.find(t => t.includeTract);

        // WORD AMOUNT
        // ~~~~~~~~~~~~~~
        let totalBonus: string = leasehold.primaryRentalTotal.toString();

        let splitBonus: string[] = !!totalBonus.includes('.') ? totalBonus.split('.') : [];

        if (splitBonus?.length === 0 || !splitBonus) {
            let output: string = MathUtil.numberToEnglish(parseInt(totalBonus, 10), ', ').trim();
            let tempOutput = output.toUpperCase();
            tempOutput += ' AND 00/100 DOLLARS';
            leaseProps.primaryRentalTotalWords = tempOutput;
        } else {
            let beginning: string = MathUtil.numberToEnglish(parseInt(splitBonus[0], 10), ' ').trim();
            let end: string = `AND ${splitBonus[1]}/100 DOLLARS`;

            let fullSentence: string = `${beginning.toUpperCase()} ${end}`;
            leaseProps.primaryRentalTotalWords = fullSentence;
        }
        // ~~~~~~~~~~~~~~

        let renderClauseIndex = 0;
        if (contractClauses?.length > 0 && lease.contractClauses?.length > 0) {
            let renderClauses = contractClauses.filter(cc => lease.contractClauses.find(lc => lc.contractAddendumClauseId === cc.id) !== undefined);
            leaseProps.additionalProvisions = renderClauses?.map((rc: ContractAddendumClause, index: number) => {
                return `${index + 1}) ${rc.clauseText}`;
            });
            renderClauseIndex = renderClauses.length;
        }

        if (lease.adHocClauses?.length > 0 && leaseClauseTypes?.length > 0) {
            leaseProps.adHocClauses = lease.adHocClauses.map((ahc: LeaseClause, index: number) => {
                let type: string = leaseClauseTypes.find((lct) => lct.id === ahc.leaseClauseTypeId)?.name;
                let currentIndex = index + 1 + renderClauseIndex;
    
                return `${currentIndex + 1}) ${type}: ${ahc.clauseText}`;
            });
        }

        if (!!location) {
            let counties: Array<string> | undefined = location.counties?.filter((c) => !!c);
            let stringCounties: string = '';
    
            if (!!counties && counties.length > 1) {
                for (let i = 0; i < counties.length; i++) {
                    if (!!counties[i] && i !== counties.length - 1) {
                        stringCounties += counties[i] + ', ';
                    }
                    if (!!counties[i] && i === counties.length - 1) {
                        stringCounties += 'and ' + counties[i] + ' Counties';
                    }
                }
                leaseProps.locationCounties = stringCounties;
            } else if (!!counties && counties.length === 1) {
                leaseProps.locationCounties = `${counties[0]}`;
            } else {
                leaseProps.locationCounties = '';
            }

            leaseProps.locationState = location.state;
            leaseProps.locationCountiesUpper = leaseProps.locationCounties.toUpperCase();
        }

        return leaseProps;
    };
}



// WEBPACK FOOTER //
// ./src/models/leaseTemplateProps/SaltwerxBrineLeaseProps.ts